import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  Home, Tag, Clock, LayoutGrid, Store, Heart, MapPinned, PlusCircle, User,
  BarChart3, Crown, LogIn, LogOut, BookOpen, ShieldAlert, Ticket, Bell
} from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useConfig } from "../../hooks/useConfig";
import { isProActive, planTier } from "../../utils/checkout";

const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const cfg = useConfig();
  const pricingPublic = cfg?.pricingPublic !== false; // default public until told otherwise
  // Unread feedback and enquiries have to be visible from anywhere, or they quietly rot.
  const [newInbox, setNewInbox] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") { setNewInbox(0); return; }
    const load = () => api.get("/admin/summary").then((r) => setNewInbox(r.data.newInbox || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user]);

  const nav = [
    { name: t("nav.home"), path: "/", icon: Home, end: true },
    { name: t("nav.browse"), path: "/browse", icon: Tag },
    { name: t("nav.nearExpiry"), path: "/near-expiry", icon: Clock },
    { name: t("nav.categories"), path: "/categories", icon: LayoutGrid },
    { name: t("nav.businesses"), path: "/businesses", icon: Store },
    { name: t("nav.favourites"), path: "/favourites", icon: Heart },
    ...(user ? [{ name: t("nav.following"), path: "/following", icon: Bell }] : []),
    ...(!user || user.role === "shopper" ? [{ name: t("nav.myDeals"), path: "/my-deals", icon: Ticket }] : []),
    { name: t("nav.lytemap"), path: "/lytemap", icon: MapPinned },
    ...(user?.role !== "shopper" ? [{ name: t("nav.postDeal"), path: "/post-deal", icon: PlusCircle }] : []),
    ...(user?.role === "business" ? [{ name: t("nav.analytics"), path: "/analytics", icon: BarChart3 }] : []),
    ...(user?.role === "admin" ? [{ name: t("nav.admin"), path: "/admin", icon: ShieldAlert, badge: newInbox }] : []),
    ...((pricingPublic || user?.role === "business" || user?.role === "admin") ? [{ name: t("nav.pricing"), path: "/pricing", icon: Crown }] : []),
    { name: t("nav.ourStory"), path: "/our-story", icon: BookOpen },
    { name: t("nav.myAccount"), path: user?.role === "admin" ? "/admin?tab=account" : "/account", icon: User },
  ];

  return (
    <aside className="h-full w-[268px] bg-card border-r border-border flex flex-col" data-testid="sidebar">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 px-6 py-4">
        <img src="/lp-logo.webp" alt="LytePress" className="h-10 w-10 rounded-full object-cover shadow-sm border border-border" />
        <span>
          <span className="block text-xl font-extrabold text-foreground leading-none">LytePress</span>
          <span className="block text-[11px] text-[#16a34a] font-semibold mt-0.5">{t("brand.tagline")}</span>
        </span>
      </Link>

      <div className="relative flex-1 min-h-0">
        <nav className="h-full px-4 space-y-0.5 overflow-y-auto lp-sidebar-scroll pb-6" data-testid="sidebar-nav">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.name} to={item.path} end={item.end} onClick={onNavigate}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 rounded-xl text-[15px] font-medium transition-colors ${
                    isActive ? "bg-[#e7f6ec] text-[#16a34a] dark:bg-[#16341f] dark:text-[#4ade80]" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}>
                <Icon size={19} />
                <span className="flex-1">{item.name}</span>
                {item.badge > 0 && (
                  <span data-testid="admin-inbox-badge"
                    className="min-w-5 h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-extrabold flex items-center justify-center">
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        {/* Fade hints that the menu keeps going on short screens. */}
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-card to-transparent" />
      </div>

      <div className="p-3 space-y-2 border-t border-border">
        {user ? (
          <div className="rounded-2xl bg-secondary p-3 flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-[#16a34a] text-white font-bold flex items-center justify-center">{(user.businessName || user.name)?.[0] || "L"}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold text-foreground truncate">{user.businessName || user.name}</span>
              <span className="block text-xs text-muted-foreground truncate">{user.email}</span>
            </span>
            <button onClick={() => { logout(); onNavigate && onNavigate(); }} data-testid="sidebar-logout-btn" title={t("sb.logOut")} className="text-muted-foreground hover:text-rose-500 shrink-0" aria-label={t("sb.logOut")}><LogOut size={18} /></button>
          </div>
        ) : (
          <div className="space-y-2">
            <Link to="/login" onClick={onNavigate} data-testid="sidebar-login-btn" className="flex items-center justify-center gap-2 w-full bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"><LogIn size={16} /> {t("sidebar.businessLogin")}</Link>
            <Link to="/signup" onClick={onNavigate} data-testid="sidebar-signup-link" className="block text-center text-xs font-semibold text-[#16a34a] hover:underline">{t("sidebar.signupFree")}</Link>
          </div>
        )}

        {user?.role === "business" && (
          isProActive(user) ? (
            <div className="rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] p-3 flex items-center gap-2" data-testid="pro-badge">
              <Crown size={16} className="text-[#16a34a] shrink-0" />
              <div className="min-w-0"><span className="block font-bold text-xs text-foreground">{planTier(user) === "business_plus" ? t("sidebar.plusActive") : t("sidebar.proActive")}</span><span className="block text-[11px] text-muted-foreground truncate">{user.planLifetime ? t("sidebar.forLife") : t("sidebar.until", { date: new Date(user.planExpiresAt).toLocaleDateString() })}</span></div>
            </div>
          ) : (
            <div className="rounded-2xl bg-[#e7f6ec] dark:bg-[#16341f] p-3">
              <div className="flex items-center gap-2 mb-1"><Crown size={16} className="text-[#16a34a]" /><span className="font-bold text-xs text-foreground">{t("sidebar.growPro")}</span></div>
              <Link to="/premium" onClick={onNavigate} data-testid="go-premium-btn" className="block text-center w-full bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-semibold py-2 rounded-xl transition-colors">{t("sidebar.goPremium")}</Link>
            </div>
          )
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
